import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, TextField, InputAdornment,
  Menu, MenuItem, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Grid, FormControl,
  InputLabel, Select,
} from '@mui/material';
import {
  Search as SearchIcon, MoreVert as MoreVertIcon, Edit as EditIcon,
  Add as AddIcon, PointOfSale as BalcaoIcon, Person as PersonIcon,
} from '@mui/icons-material';
import wagService, { WAGBalcao, WAGAgencia } from '../../services/wag.service';

const WAGBalcoes: React.FC = () => {
  const [agencias, setAgencias] = useState<WAGAgencia[]>([]);
  const [agenciaId, setAgenciaId] = useState('');
  const [items, setItems] = useState<WAGBalcao[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState<WAGBalcao | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<{ numero: string; desig: string }>({ numero: '', desig: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Carregar agências
  useEffect(() => {
    wagService.getAgencias().then(data => {
      const activas = data.filter(a => a.estado === 'A');
      setAgencias(activas);
      if (activas.length > 0) setAgenciaId(activas[0].id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!agenciaId) return;
    setLoading(true);
    setError(null);
    try {
      setItems(await wagService.getBalcoes(agenciaId));
    } catch {
      setError('Erro ao carregar balcões');
    } finally {
      setLoading(false);
    }
  }, [agenciaId]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    !search || i.desig.toLowerCase().includes(search.toLowerCase()) || String(i.numero).includes(search)
  );

  async function handleCreate() {
    setSaving(true);
    setSaveError(null);
    try {
      await wagService.createBalcao(agenciaId, {
        numero: parseInt(form.numero),
        desig: form.desig,
      });
      setCreateOpen(false);
      setForm({ numero: '', desig: '' });
      load();
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Erro ao criar balcão');
    } finally {
      setSaving(false);
    }
  }

  function openCreate() {
    // Sugerir próximo número
    const maxNum = items.length > 0 ? Math.max(...items.map(b => b.numero)) : 0;
    setForm({ numero: String(maxNum + 1), desig: `Balcão ${maxNum + 1}` });
    setSaveError(null);
    setCreateOpen(true);
  }

  const agenciaActual = agencias.find(a => a.id === agenciaId);

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Balcões WAG</Typography>
          <Typography variant="body2" color="text.secondary">Guichets de atendimento por agência</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          disabled={!agenciaId}
          sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
          Novo Balcão
        </Button>
      </Box>

      {/* Selector de agência */}
      {agencias.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Agência</InputLabel>
            <Select value={agenciaId} label="Agência" onChange={e => setAgenciaId(e.target.value)}>
              {agencias.map(a => (
                <MenuItem key={a.id} value={a.id}>
                  {a.desig} <Chip label={a.codigo} size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {agenciaActual && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {agenciaActual.totalBalcoes} balcão(ões) configurado(s)
            </Typography>
          )}
        </Paper>
      )}

      {agencias.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Crie primeiro uma agência antes de configurar balcões.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {agenciaId && (
        <>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <TextField fullWidth placeholder="Pesquisar por número ou designação…" value={search}
              onChange={e => setSearch(e.target.value)} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Nº</TableCell>
                  <TableCell>Designação</TableCell>
                  <TableCell>Operador activo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {search ? 'Nenhum balcão encontrado' : 'Esta agência ainda não tem balcões. Crie o primeiro.'}
                    </TableCell>
                  </TableRow>
                ) : filtered.map(b => (
                  <TableRow key={b.id} hover>
                    <TableCell>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: 1.5,
                        bgcolor: '#e9456022', color: '#e94560',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, fontSize: 16,
                      }}>
                        {b.numero}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BalcaoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography fontWeight={600}>{b.desig}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {b.operadorActivo ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main" fontWeight={500}>Em sessão</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={b.estado === 'A' ? 'Activo' : 'Inactivo'}
                        size="small"
                        color={b.estado === 'A' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={e => { setSelected(b); setAnchorEl(e.currentTarget); }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Context menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => {
          setForm({ numero: String(selected?.numero ?? ''), desig: selected?.desig ?? '' });
          setSaveError(null);
          setEditOpen(true);
          setAnchorEl(null);
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />Editar designação
        </MenuItem>
      </Menu>

      {/* Dialog Criar */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Novo Balcão — {agenciaActual?.desig}</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={4}>
              <TextField label="Número *" type="number" fullWidth size="small"
                value={form.numero}
                onChange={e => {
                  const n = e.target.value;
                  setForm(p => ({ ...p, numero: n, desig: p.desig === `Balcão ${p.numero}` ? `Balcão ${n}` : p.desig }));
                }} />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Designação *" fullWidth size="small"
                value={form.desig}
                onChange={e => setForm(p => ({ ...p, desig: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={saving || !form.numero || !form.desig}
            sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}>
            {saving ? 'A criar…' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Editar (só designação — o número não muda) */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar Balcão {selected?.numero}</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <TextField label="Designação" fullWidth size="small" sx={{ mt: 1 }}
            value={form.desig}
            onChange={e => setForm(p => ({ ...p, desig: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving || !form.desig}
            sx={{ bgcolor: '#e94560', '&:hover': { bgcolor: '#c73350' } }}
            onClick={async () => {
              // TODO: endpoint PUT balcão — por agora só fecha
              setEditOpen(false);
            }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WAGBalcoes;
